// src/components/pickers/PartPickerModal.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Alert,
  Row,
  Col,
} from "react-bootstrap";
import { authFetch } from "../utils/authFetch";
import api from "../../axiosConfig";

const TIP_OPTIONS = [
  { value: 1, label: "Servicii primite" },
  { value: 2, label: "Obiecte" },
  { value: 3, label: "Servicii prestate" },
];

async function authGetJson(url) {
  const r = await authFetch(url);

  // deja JSON (în cazul în care authFetch întoarce direct obiect)
  if (r && typeof r === "object" && !("ok" in r) && !("json" in r)) return r;

  // Response
  if (r?.ok) return await r.json();

  const msg = r?.text ? await r.text() : "Request failed";
  throw new Error(msg);
}

export default function PartPickerModal({
  show,
  onHide,
  onPick,
  initialQuery = "",
  minChars = 0, // implicit 0 caractere pentru căutare automată
  filter = {}, // ex: { tip_grupa_produse: 1 } pentru servicii
}) {
  const [view, setView] = useState("list"); // "list" sau "create"
  const [q, setQ] = useState(initialQuery);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [page, setPage] = useState(1);

  const loadingPageRef = useRef(null);
  const lastFetchedQueryRef = useRef(null);

  // State-uri pentru adăugare articol nou
  const [umOptions, setUmOptions] = useState([]);
  const [grupaOptions, setGrupaOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [createErrors, setCreateErrors] = useState({});
  const [createForm, setCreateForm] = useState({
    cod: "",
    denumire: "",
    um_id: "",
    grupa_produse_id: "",
    tip_grupa_produse: "",
    cpv: "",
    nc8: "",
    ean: "",
    kg_net: 0,
    kg_brut: 0,
  });

  const limit = 20;

  // Încărcare UM-uri și Grupe la deschiderea modalului
  useEffect(() => {
    if (!show) return;

    api
      .get("/bxums", { params: { limit: 500 } })
      .then((res) => setUmOptions(res.data?.items ?? res.data ?? []))
      .catch(() => setUmOptions([]));

    api
      .get("/grupa_produse", { params: { limit: 500 } })
      .then((res) => setGrupaOptions(res.data?.items ?? res.data ?? []))
      .catch(() => setGrupaOptions([]));
  }, [show]);

  const fetchPage = async (nextPage = 1, query = q) => {
    const qq = (query || "").trim();
    if (qq.length < minChars) {
      setItems([]);
      setTotal(null);
      setPage(1);
      setErr("");
      return;
    }

    if (loadingPageRef.current === nextPage) {
      return;
    }
    loadingPageRef.current = nextPage;

    setBusy(true);
    setErr("");
    try {
      let url;
      const isStockSearch = filter && filter.gestiune_id != null;
      if (isStockSearch) {
        const params = new URLSearchParams({
          gestiune_id: String(filter.gestiune_id),
          skip: String((nextPage - 1) * limit),
          limit: String(limit),
          q: qq,
        });
        url = `/stock/stocin/search?${params.toString()}`;
      } else {
        const params = new URLSearchParams({
          skip: String((nextPage - 1) * limit),
          limit: String(limit),
          q: qq,
        });
        if (filter) {
          Object.entries(filter).forEach(([k, v]) => {
            if (v !== undefined && v !== null) {
              params.set(k, String(v));
            }
          });
        }
        url = `/bxpart/?${params.toString()}`;
      }

      const data = await authGetJson(url);

      const list = data?.items || data || [];
      const t = isStockSearch ? list.length : (data?.total ?? null);

      const mapped = list.map((it) => {
        const id = it.id ?? it.bxid ?? it.bxpart_id;
        const code = it.cod ?? it.bxcod ?? it.sku ?? "";
        const name = it.denumire ?? it.bxname ?? it.name ?? it.descriere ?? "";
        const label = `${code ? code + " • " : ""}${name || "#" + id}`;
        const raw = {
          ...it,
          id: id,
          tip_grupa_produse: it.tip_grupa_produse ?? (isStockSearch ? 2 : 3),
        };
        return { id, label, raw };
      });

      setItems((prev) => (nextPage === 1 ? mapped : [...prev, ...mapped]));
      setTotal(t);
      setPage(nextPage);
      lastFetchedQueryRef.current = qq;
    } catch (e) {
      setErr(String(e?.message || e));
      if (nextPage === 1) {
        setItems([]);
        setTotal(null);
      }
    } finally {
      setBusy(false);
      loadingPageRef.current = null;
    }
  };

  // Reset form adăugare
  const resetCreateForm = () => {
    setCreateForm({
      cod: "",
      denumire: "",
      um_id: "",
      grupa_produse_id: "",
      tip_grupa_produse: "",
      cpv: "",
      nc8: "",
      ean: "",
      kg_net: 0,
      kg_brut: 0,
    });
    setCreateErrors({});
    setCreateErr("");
  };

  // Inițializare modal
  useEffect(() => {
    if (!show) return;

    setView("list");
    resetCreateForm();
    const initQ = initialQuery || "";
    setQ(initQ);
    setItems([]);
    setTotal(null);
    setPage(1);
    setErr("");
    lastFetchedQueryRef.current = null;

    if (initQ.trim().length >= minChars) {
      fetchPage(1, initQ);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Căutare automată la tastare (debounce)
  useEffect(() => {
    if (!show || view !== "list") return;

    const query = q.trim();
    if (query.length < minChars) {
      setItems([]);
      setTotal(null);
      setPage(1);
      return;
    }

    if (lastFetchedQueryRef.current === query) {
      return;
    }

    const t = setTimeout(() => {
      fetchPage(1, q);
    }, 300);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, show, view]);

  const hasMore = total != null ? page * limit < total : items.length === limit;

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 15) {
      if (!busy && hasMore) {
        fetchPage(page + 1, q);
      }
    }
  };

  const pick = (opt) => {
    if (!opt) return;

    const code = opt.raw?.cod ?? "";
    const name = opt.raw?.denumire ?? "";
    const label = `${code ? code + " • " : ""}${name}`.trim();

    onPick?.({
      id: opt.id,
      label,
      raw: opt.raw,
    });

    onHide?.();
  };

  // Handlers adăugare articol
  const handleCreateChange = (e) => {
    const { name, value, type } = e.target;
    setCreateForm((f) => ({
      ...f,
      [name]: type === "number" ? (value === "" ? "" : Number(value)) : value,
    }));
    if (createErrors[name])
      setCreateErrors((er) => ({ ...er, [name]: undefined }));
  };

  const handleGrupaChange = (e) => {
    const gid = e.target.value;
    const g = grupaOptions.find((opt) => String(opt.id) === String(gid));
    setCreateForm((f) => ({
      ...f,
      grupa_produse_id: gid,
      tip_grupa_produse: g ? g.tip_grupa : "",
    }));
    if (createErrors.grupa_produse_id) {
      setCreateErrors((er) => ({ ...er, grupa_produse_id: undefined }));
    }
    if (createErrors.tip_grupa_produse) {
      setCreateErrors((er) => ({ ...er, tip_grupa_produse: undefined }));
    }
  };

  const validateCreate = () => {
    const er = {};
    if (!createForm.cod.trim()) er.cod = "Codul este obligatoriu.";
    if (!createForm.denumire.trim())
      er.denumire = "Denumirea este obligatorie.";
    if (!createForm.um_id) er.um_id = "Alegeți unitatea de măsură.";
    if (!createForm.grupa_produse_id)
      er.grupa_produse_id = "Alegeți grupa de produse.";
    if (!createForm.tip_grupa_produse)
      er.tip_grupa_produse = "Tipul grupei este obligatoriu.";
    setCreateErrors(er);
    return Object.keys(er).length === 0;
  };

  const handleSavePart = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Previne submit-ul formularului părinte
    if (!validateCreate()) return;

    setSubmitting(true);
    setCreateErr("");

    const payload = {
      ...createForm,
      um_id: Number(createForm.um_id),
      grupa_produse_id: Number(createForm.grupa_produse_id),
      tip_grupa_produse: Number(createForm.tip_grupa_produse),
      kg_net: Number(createForm.kg_net) || 0,
      kg_brut: Number(createForm.kg_brut) || 0,
      cpv: createForm.cpv || "",
      nc8: createForm.nc8 || "",
      ean: createForm.ean || "",
    };

    try {
      const { data } = await api.post("/bxpart", payload);

      const code = data.cod ?? "";
      const name = data.denumire ?? "";
      const label = `${code ? code + " • " : ""}${name}`.trim();

      onPick?.({
        id: data.id,
        label,
        raw: data,
      });

      setView("list");
      resetCreateForm();
      onHide?.();
    } catch (err) {
      const detail = err?.response?.data;
      let msg = err?.message || "Eroare necunoscută";

      if (Array.isArray(detail)) {
        msg = detail
          .map((e) => `${(e.loc || []).join(".")}: ${e.msg}`)
          .join("\n");
      } else if (detail?.detail) {
        msg =
          typeof detail.detail === "string"
            ? detail.detail
            : JSON.stringify(detail.detail);
      }
      setCreateErr("Eroare la salvare:\n" + msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {view === "create" ? "Adaugă Articol Nou" : "Alege articol"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {view === "list" ? (
          <>
            {err && <Alert variant="danger">{err}</Alert>}

            <div className="d-flex gap-2">
              <Form.Control
                size="sm"
                style={{ fontSize: "13px", height: "35px" }}
                autoFocus
                placeholder={
                  minChars >= 2
                    ? `Caută articol... (minim ${minChars} caractere)`
                    : "Caută articol..."
                }
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    fetchPage(1, q);
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                style={{
                  fontSize: "13px",
                  height: "35px",
                  whiteSpace: "nowrap",
                }}
                disabled={busy}
                onClick={() => fetchPage(1, q)}
              >
                Refresh
              </Button>
              <Button
                type="button"
                variant="success"
                size="sm"
                style={{
                  fontSize: "13px",
                  height: "35px",
                  whiteSpace: "nowrap",
                }}
                onClick={() => setView("create")}
              >
                + Articol Nou
              </Button>
            </div>

            <div className="mt-3">
              {busy && page === 1 && items.length === 0 ? (
                <div className="d-flex align-items-center gap-2">
                  <Spinner size="sm" />
                  <span>Se încarcă...</span>
                </div>
              ) : (
                <>
                  <div
                    style={{ maxHeight: "400px", overflowY: "auto" }}
                    onScroll={handleScroll}
                  >
                    <Table hover size="sm" className="mb-0">
                      <tbody>
                        {items.map((it) => (
                          <tr
                            key={String(it.id)}
                            role="button"
                            onClick={() => pick(it)}
                            style={{ cursor: "pointer" }}
                          >
                            <td style={{ width: 120, whiteSpace: "nowrap" }}>
                              #{it.id}
                            </td>
                            <td>{it.label}</td>
                          </tr>
                        ))}

                        {items.length === 0 && !busy && (
                          <tr>
                            <td className="text-muted">
                              {(q || "").trim().length < minChars
                                ? `Tastează cel puțin ${minChars} caractere pentru căutare automată.`
                                : "Nimic găsit."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {busy && page > 1 && (
                    <div className="d-flex align-items-center gap-2 mt-2 justify-content-center">
                      <Spinner size="sm" />
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        Se încarcă mai multe...
                      </span>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {total != null ? `Total: ${total}` : ""}
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <Form onSubmit={handleSavePart} noValidate>
            {createErr && (
              <Alert variant="danger" style={{ whiteSpace: "pre-line" }}>
                {createErr}
              </Alert>
            )}

            <Row className="g-3">
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>
                    Cod <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="cod"
                    value={createForm.cod}
                    onChange={handleCreateChange}
                    isInvalid={!!createErrors.cod}
                    placeholder="Cod articol / SKU"
                  />
                  <Form.Control.Feedback type="invalid">
                    {createErrors.cod}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>
                    Denumire <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="denumire"
                    value={createForm.denumire}
                    onChange={handleCreateChange}
                    isInvalid={!!createErrors.denumire}
                    placeholder="Denumire articol"
                  />
                  <Form.Control.Feedback type="invalid">
                    {createErrors.denumire}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>
                    Unitate de Măsură <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="um_id"
                    value={createForm.um_id}
                    onChange={handleCreateChange}
                    isInvalid={!!createErrors.um_id}
                  >
                    <option value="">— alege —</option>
                    {umOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.cod} — {opt.descriere || opt.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {createErrors.um_id}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>
                    Grupă produse <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="grupa_produse_id"
                    value={createForm.grupa_produse_id}
                    onChange={handleGrupaChange}
                    isInvalid={!!createErrors.grupa_produse_id}
                  >
                    <option value="">— alege —</option>
                    {grupaOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.cod || opt.code} — {opt.denumire || opt.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {createErrors.grupa_produse_id}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Tip grupă (auto-dedus)</Form.Label>
                  <Form.Select
                    name="tip_grupa_produse"
                    value={createForm.tip_grupa_produse}
                    disabled
                  >
                    <option value="">— nesetat —</option>
                    {TIP_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>CPV</Form.Label>
                  <Form.Control
                    name="cpv"
                    value={createForm.cpv}
                    onChange={handleCreateChange}
                    placeholder="Cod CPV"
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>NC8</Form.Label>
                  <Form.Control
                    name="nc8"
                    value={createForm.nc8}
                    onChange={handleCreateChange}
                    placeholder="Cod NC8"
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>EAN</Form.Label>
                  <Form.Control
                    name="ean"
                    value={createForm.ean}
                    onChange={handleCreateChange}
                    placeholder="Cod bare EAN"
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Greutate netă (kg)</Form.Label>
                  <Form.Control
                    type="number"
                    name="kg_net"
                    value={createForm.kg_net}
                    onChange={handleCreateChange}
                    min="0"
                    step="0.01"
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Greutate brută (kg)</Form.Label>
                  <Form.Control
                    type="number"
                    name="kg_brut"
                    value={createForm.kg_brut}
                    onChange={handleCreateChange}
                    min="0"
                    step="0.01"
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setView("list");
                  resetCreateForm();
                }}
                disabled={submitting}
              >
                Renunță
              </Button>
              <Button type="submit" variant="dark" disabled={submitting}>
                {submitting ? "Se salvează…" : "Salvează"}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Închide
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
